// Reply, Reply all and Forward for a thread (the reader, a card's thread drawer, the active
// card): the server starts a draft addressed the way a card's reply is (Reply all keeps everyone
// on To and Cc), and the compose sheet opens on it. Nothing is sent from here; sending is the
// sheet's arm → confirm. Keys: w, a, f on a thread; all three in ⌘K.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Forward, Reply, ReplyAll } from "lucide-react";
import { toast } from "sonner";
import type { ComposeStartRequest } from "@shared/api";
import { isApiError } from "@web/lib/api";
import { composeApi } from "@web/lib/composeApi";
import { acceptDraft, invalidateCompose } from "@web/lib/composeQueries";
import { useCommand } from "@web/shell/commands";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { composeStore } from "./composeStore";

type Mode = ComposeStartRequest["mode"];

let starting = false;

/** Asks the server for the draft, then opens it in the sheet. One at a time. */
export async function startCompose(
  queryClient: ReturnType<typeof useQueryClient>,
  threadKey: string,
  mode: Mode,
): Promise<void> {
  if (starting) return;
  starting = true;
  try {
    const started = await composeApi.start({ threadKey, mode });
    acceptDraft(queryClient, started.draft);
    invalidateCompose(queryClient);
    composeStore.open({
      accountId: started.draft.accountId,
      draftId: started.draft.id,
      replyToThreadKey: started.draft.replyToThreadKey ?? undefined,
      threadSubject: started.threadSubject,
      mode,
      startedRev: started.draft.rev,
      notice: started.notice,
    });
  } catch (error) {
    toast(
      isApiError(error) && error.status === 404
        ? "That conversation isn't here any more."
        : "Couldn't start that just now. Nothing was sent.",
    );
  } finally {
    starting = false;
  }
}

/** Registers w / a / f (and the palette entries) for this thread while `threadKey` is set. */
export function useReplyCommands(threadKey: string | null): void {
  const queryClient = useQueryClient();
  const on = !!threadKey;
  const start = (mode: Mode) => {
    if (threadKey) void startCompose(queryClient, threadKey, mode);
  };
  useCommand("thread.reply", () => start("reply"), on);
  useCommand("thread.replyAll", () => start("replyAll"), on);
  useCommand("thread.forward", () => start("forward"), on);
}

export function ReplyActions({
  threadKey,
  replyAll,
  compact = false,
  keys = true,
}: {
  threadKey: string;
  /** Offer Reply all (more than one other person on the thread). */
  replyAll: boolean;
  /** Icons only on small screens. */
  compact?: boolean;
  /** Show the keycaps (where w / a / f are bound to this thread). */
  keys?: boolean;
}) {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<Mode | null>(null);
  const run = (mode: Mode) => {
    setBusy(mode);
    void startCompose(queryClient, threadKey, mode).finally(() => setBusy(null));
  };
  const label = (text: string) => (
    <span className={compact ? "hidden sm:inline" : undefined}>{text}</span>
  );
  return (
    <div className="flex items-center gap-1.5" role="group" aria-label="Answer this thread">
      <Button
        size="sm"
        variant="secondary"
        busy={busy === "reply"}
        onClick={() => run("reply")}
        aria-keyshortcuts={keys ? "w" : undefined}
        data-testid="reply"
      >
        <Reply aria-hidden className="size-4" />
        {label("Reply")}
        {keys ? <Kbd className="hidden sm:inline-flex">W</Kbd> : null}
      </Button>
      {replyAll ? (
        <Button
          size="sm"
          variant="ghost"
          busy={busy === "replyAll"}
          onClick={() => run("replyAll")}
          aria-keyshortcuts={keys ? "a" : undefined}
          data-testid="reply-all"
        >
          <ReplyAll aria-hidden className="size-4" />
          {label("Reply all")}
          {keys ? <Kbd className="hidden sm:inline-flex">A</Kbd> : null}
        </Button>
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        busy={busy === "forward"}
        onClick={() => run("forward")}
        aria-keyshortcuts={keys ? "f" : undefined}
        data-testid="forward"
      >
        <Forward aria-hidden className="size-4" />
        {label("Forward")}
        {keys ? <Kbd className="hidden sm:inline-flex">F</Kbd> : null}
      </Button>
    </div>
  );
}

/** More than one person besides the owner wrote or was addressed: Reply all means something. */
export function hasOthers(
  messages: readonly {
    from: { email: string } | null;
    to: { email: string }[];
    cc: { email: string }[];
  }[],
  ownerEmail: string,
): boolean {
  const owner = ownerEmail.toLowerCase();
  const people = new Set<string>();
  for (const m of messages)
    for (const a of [...(m.from ? [m.from] : []), ...m.to, ...m.cc]) {
      const email = a.email.toLowerCase();
      if (email !== owner) people.add(email);
    }
  return people.size > 1;
}
